import { useMemo } from 'react';

import { CreateModal as GenericCreateModal } from '@admin/organisms/CreateModal';
import { AdminRoute } from '@admin/routing';
import { getArtikelThemeConfig } from '@admin/templates/artikel/Artikel.config';
import { type NewsCategory, type NewsItemActionCreate, NewsStatus } from '@common/clients/api';
import { useContextData } from '@common/useContextData';
import { useUserContext } from '@common/useUserContext/UserContextProvider';

import { createNews } from '../helpers/createNews';
import { formFields } from '../helpers/formFields';
import { type ArticleItem, type NewsActionCreateItem } from '../types';

interface Props {
    categories: NewsCategory[];
    __translationKey: string;
}

export const CreateModal = ({ categories, __translationKey }: Props) => {
    const contextData = useContextData();
    const userContext = useUserContext();
    const themeConfig = getArtikelThemeConfig(contextData);

    const getCategoryID = (slug: string) => {
        const category = categories.find((catobj: NewsCategory) => catobj.slug === slug);
        return category?.id;
    };

    const submit = async (data: NewsActionCreateItem): Promise<void> => {
        const requestBody: NewsItemActionCreate = {
            newsTitle: data.newsTitle,
            categoryID: getCategoryID(data.category),
            commentCount: 0,
            status: NewsStatus.DRAFT,
        };

        const response = await createNews({
            contextData,
            userContext,
            requestBody,
        });

        if (!response.newsID) {
            return;
        }

        const activeParam = themeConfig.defaultStatus === NewsStatus.PUBLISHED ? 1 : 0;
        const route = response.category?.isMedia ? AdminRoute.Media : AdminRoute.Article;
        window.location.href = `/${route}/${response.newsID}?active=${activeParam}`;
    };

    const fields = useMemo(() => formFields(categories), [categories]);

    return (
        <GenericCreateModal<ArticleItem, NewsActionCreateItem>
            formFields={fields}
            submit={submit}
            __translationKey={__translationKey}
        />
    );
};
